import { useQuery } from '@apollo/client';
import { buttonV2Class } from '@synoptic/ui-kit/button-v2/button-v2.css.js';
import { ArrowRightIcon } from '@synoptic/ui-kit/icons/react/arrow-right.js';
import { SkeletonV2 } from '@synoptic/ui-kit/skeleton-v2/skeleton-v2.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { useId } from 'react';
import { gql } from '@/__generated__/index.ts';
import { StreamCardLinkV2 } from '@/components/stream-card-v2/stream-card-link-v2.tsx';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { useAppLoader } from '@/routes/_app/loader.ts';
import {
  exploreCardGridV2,
  exploreCustomCardSkeletonV2,
  exploreSectionHeaderV2,
  exploreSectionHeadingV2,
  exploreSectionSubtitleV2,
  exploreSectionTitleV2,
  exploreSectionV2,
} from './explore-page-v2.css.ts';

const ExploreCustomStreamsQuery = gql(`
  query ExploreCustomStreamsQuery($includeViewerTargets: Boolean!) {
    streamsAll(
      limit: 4
      sortBy: CREATED_AT
      custom: true
      minFollowers: 0
      nextToken: null
    ) {
      items {
        ...StreamCardFragment
        following @include(if: $includeViewerTargets)
        customTargets @include(if: $includeViewerTargets)
      }
      nextToken
    }
  }
`);

const SKELETON_COUNT = 4;

const renderSkeletons = () =>
  Array.from({ length: SKELETON_COUNT }, (_, index) => (
    <SkeletonV2
      key={index}
      className={exploreCustomCardSkeletonV2}
      height={238.58}
      width="100%"
    />
  ));

export const ExploreCustomStreamsSectionV2 = () => {
  const titleId = useId();
  const toast = useToast();
  const { authenticated } = useAppLoader();
  const { data, loading } = useQuery(ExploreCustomStreamsQuery, {
    variables: {
      includeViewerTargets: authenticated,
    },
    fetchPolicy: 'cache-and-network',
    onError: () => {
      toast.error({
        title: 'Failed to fetch streams',
      });
    },
  });

  const streams = data?.streamsAll?.items;
  const showSkeletons = loading && !streams?.length;

  return (
    <section className={exploreSectionV2} aria-labelledby={titleId}>
      <header className={exploreSectionHeaderV2}>
        <div className={exploreSectionHeadingV2}>
          <h2 id={titleId} className={exploreSectionTitleV2}>
            Custom Streams
          </h2>
          <p className={exploreSectionSubtitleV2}>
            Custom streams enable you to define the data you want to track.
          </p>
        </div>
        <Link className={buttonV2Class.secondary} to={paths.customStreams}>
          See all
          <ArrowRightIcon />
        </Link>
      </header>
      <div className={exploreCardGridV2}>
        {showSkeletons
          ? renderSkeletons()
          : streams?.map((stream) => (
              <StreamCardLinkV2 key={stream.id} stream={stream} />
            ))}
      </div>
    </section>
  );
};
